import * as realtimeBpm from 'realtime-bpm-analyzer'
import * as THREE from 'three'
import {
  HEADER_COLLAPSED_HEIGHT,
  // BLOCK_HEIGHT,
  BLOCK_BIG_HEIGHT,
  COLLAPSED_HEIGHT,
  TIMELINE_PREVIEW_OVERLAP_PX,
  ZOOM_MAX_SEC,
  ZOOM_STEP,
} from '@/etc/consts'
import axios from 'axios'
import { rangesOverlap, timelineAdjacencyEpsilon } from '@/etc/timelineMath'
// import { fa0 } from '@fortawesome/free-solid-svg-icons'

// generate random number in range
export const rnd = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

// generate random code
export const genCode = (digist) => {
  let ret = ''
  for (let i = 0; i < digist; i++) ret += `${rnd(0, 9)}`
  return ret
}

// async timeout
export const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// checks audio file BPM
export const getBPM = async (fileArrayBuffer) => {
  const audioContext = new AudioContext()
  const audio = await audioContext.decodeAudioData(fileArrayBuffer)
  const result = await realtimeBpm.analyzeFullBuffer(audio)
  return result
}

// 1000% deep cloning of json supported data. not super optimal, recommended for medium and small data only
export const jclone = (something) => {
  if (typeof something === 'undefined') return undefined
  // console.log('jclone', something)
  return JSON.parse(JSON.stringify(something))
}

/***
 *  check is strip with length 'length' can be dropped at time 't' with other 'existingStrip'
 */
export const stripCanDrop = (depth, t, length, existingStrip, eps = timelineAdjacencyEpsilon()) =>
  !(
    depth === existingStrip.depth &&
    rangesOverlap(t, length, existingStrip.start, existingStrip.length, eps)
  )

// calculates depth lines in px depending on all shit
export const depthBreakpoints = (
  depth,
  maxDepth,
  collapse,
  direction,
  includeLast = true,
  stripCollapsed = false
) => {
  const ret = [{ depth: -1, pos: 0 }]
  let pos = 0
  if (stripCollapsed) pos = -maxDepth * COLLAPSED_HEIGHT
  if (direction === 'down') {
    for (let i = 0; i <= maxDepth; i++) {
      if (collapse && i !== depth) {
        pos += COLLAPSED_HEIGHT + 1
      } else {
        pos += BLOCK_BIG_HEIGHT + 1
      }
      ret.push({ depth: i, pos })
    }
  } else {
    for (let i = maxDepth; i > 0; i--) {
      if (collapse && i !== depth) {
        pos += COLLAPSED_HEIGHT + 1
      } else {
        pos += BLOCK_BIG_HEIGHT + 1
      }
      ret.push({ depth: i, pos })
    }
  }
  if (!includeLast) return ret.slice(0, -1)
  return ret
}

export const depthGrid = (
  curDepth,
  maxDepth,
  collapse,
  direction,
  collapsed = false
) => {
  /*
  return depthBreakpoints(curDepth, maxDepth, collapse, direction, true, collapsed)
    .map(bp => ({
      depth: bp.depth,
      bottom: bp.pos
    }))
    */
  /**/
  const grid = []
  let b = 0
  if (collapsed) b -= HEADER_COLLAPSED_HEIGHT
  if (direction === 'down') {
    for (let i = 0; i <= maxDepth; i++) {
      if (collapse && i !== curDepth) {
        b += COLLAPSED_HEIGHT
      } else {
        b += BLOCK_BIG_HEIGHT
      }
      grid.push({ depth: i, bottom: b })
    }
  } else {
    for (let i = maxDepth; i >= 0; i--) {
      if (collapse && i !== curDepth) {
        b += COLLAPSED_HEIGHT
      } else {
        b += BLOCK_BIG_HEIGHT
      }
      grid.push({ depth: i, bottom: b })
    }
  }
  return grid
  /**/
}

// calculate depth for Y position of mouse (0 is bottom of header!)
export const y2depth = (y, curDepth, maxDepth, collapse, direction) => {
  const grid = depthGrid(curDepth, maxDepth, collapse, direction)
  return grid.reduce(
    (acc, bot) => {
      if (acc.depth !== -1) return acc
      if (y <= bot.bottom) return bot
      return acc
    },
    { depth: -1, bottom: 0 }
  )
}

export const getTrackOccupiedDepths = (track) => {
  const seen = new Set()
  return Object.values(track?.strips || {})
    .filter(Boolean)
    .map((strip) => Math.max(0, Number(strip?.depth) || 0))
    .filter((depth) => {
      if (seen.has(depth)) return false
      seen.add(depth)
      return true
    })
    .sort((a, b) => a - b)
}

export const resolveTrackFocusedDepth = (track, occupiedDepths = null) => {
  const occupied = Array.isArray(occupiedDepths)
    ? occupiedDepths
    : getTrackOccupiedDepths(track)
  const current = Math.max(0, Number(track?.depth) || 0)
  if (!occupied.length) return 0
  if (occupied.includes(current)) return current
  return occupied.reduce((best, depth) => {
    if (best === null) return depth
    const bestDist = Math.abs(best - current)
    const nextDist = Math.abs(depth - current)
    if (nextDist < bestDist) return depth
    if (nextDist === bestDist && depth < best) return depth
    return best
  }, null)
}

export const getRegularTrackLaneSummary = (track) => {
  const occupiedDepths = getTrackOccupiedDepths(track)
  const focusedDepth = resolveTrackFocusedDepth(track, occupiedDepths)
  const occupiedAbove = occupiedDepths.filter((depth) => depth > focusedDepth)
  const occupiedBelow = occupiedDepths.filter((depth) => depth < focusedDepth)
  const bottomSafePadPx = TIMELINE_PREVIEW_OVERLAP_PX
  return {
    occupiedDepths,
    focusedDepth,
    occupiedAbove,
    occupiedBelow,
    thinLaneCount: occupiedAbove.length + occupiedBelow.length,
    bottomSafePadPx,
  }
}

export const getRegularTrackCollapsedLaneLayout = (
  track,
  direction = 'up'
) => {
  const occupiedDepths = getTrackOccupiedDepths(track)
  const focusedDepth = resolveTrackFocusedDepth(track, occupiedDepths)
  const isUp = direction !== 'down'
  const occupiedAbove = occupiedDepths
    .filter((depth) => (isUp ? depth > focusedDepth : depth < focusedDepth))
    .sort((a, b) => (isUp ? b - a : a - b))
  const occupiedBelow = occupiedDepths
    .filter((depth) => (isUp ? depth < focusedDepth : depth > focusedDepth))
    .sort((a, b) => (isUp ? b - a : a - b))
  const bottomSafePadPx = TIMELINE_PREVIEW_OVERLAP_PX

  const slots = {}
  let top = HEADER_COLLAPSED_HEIGHT
  occupiedAbove.forEach((depth) => {
    slots[depth] = {
      top,
      height: COLLAPSED_HEIGHT,
      collapsed: true,
    }
    top += COLLAPSED_HEIGHT
  })

  const focusTop = HEADER_COLLAPSED_HEIGHT + 1 + occupiedAbove.length * COLLAPSED_HEIGHT
  const focusHeight = BLOCK_BIG_HEIGHT - 1 + (occupiedBelow.length === 0 ? bottomSafePadPx : 0)
  slots[focusedDepth] = {
    top: focusTop,
    height: focusHeight,
    collapsed: false,
  }

  top = focusTop + focusHeight
  occupiedBelow.forEach((depth) => {
    slots[depth] = {
      top,
      height: COLLAPSED_HEIGHT,
      collapsed: true,
    }
    top += COLLAPSED_HEIGHT
  })

  return {
    focusedDepth,
    occupiedDepths,
    occupiedAbove,
    occupiedBelow,
    bottomSafePadPx,
    slots,
  }
}

export const world2screen = (x, y, z, camera, screenElement) => {
  const screenRect = screenElement.getBoundingClientRect()
  const vector = new THREE.Vector3(x, y, z)
  vector.project(camera) // `camera` is a THREE.PerspectiveCamera
  const xpx = Math.round(
    (0.5 + vector.x / 2) * screenRect.width
  )
  const ypx = Math.round(
    (0.5 - vector.y / 2) * screenRect.height
  )
  return { x: xpx, y: ypx }
}

// screen to world convertor
export const screen2world = (x, y, camera, width, height, targetZ = 0) => {
  // console.log('screen2world', [x, y, camera, width, height, targetZ])
  const ndcX = (x / width) * 2 - 1
  const ndcY = -(y / height) * 2 + 1
  const ndcVector = new THREE.Vector3(ndcX, ndcY, 1)
  ndcVector.unproject(camera)
  const direction = ndcVector.sub(camera.position).normalize()
  const distance = (targetZ - camera.position.z) / direction.z
  const worldPosition = camera.position
    .clone()
    .add(direction.multiplyScalar(distance))
  worldPosition.z = targetZ
  return worldPosition
}

// window available size
export const windowRect = () => {
  const html = document.getElementsByTagName('html')[0]
  return {
    width: html ? html.clientWidth : window.innerWidth,
    height: html ? html.clientHeight : window.innerHeight,
  }
}

// download file
export const downloadFile = (byteArray, type, name) => {
  const blb = new Blob([byteArray], { type })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blb)
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const blobFromUrl = async (url) => {
  // console.log('blob from url', url, type)
  const res = await axios({
    url,
    method: 'GET',
    responseType: 'arraybuffer',
  })
  // console.log('DOWN', res)
  const blob = res.data || null
  return blob
}

export const map2 = (num, inMin, inMax, outMin, outMax) =>
  ((num - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin

export const p2pDistance = (p0, p1) => {
  const v0 = new THREE.Vector2(p0.x, p0.y)
  const v1 = new THREE.Vector2(p1.x, p1.y)
  return v0.distanceTo(v1)
}

export const blob2base64 = (blob, mimeType) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const dataUrlPrefix = `data:${mimeType};base64,`
      const base64WithDataUrlPrefix = reader.result
      const base64 = base64WithDataUrlPrefix.replace(dataUrlPrefix, '')
      console.log('blob2base64 making NO withDataUrlPrefix', {
        mimeType,
        base64,
      })
      //resolve(base64)
      resolve(base64WithDataUrlPrefix)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export const bitsArray = (state, from, to) => {
  const qps = state.settings.qps
  const spl = state.settings.spl
  // use BeatCode for precise quarter-note seconds (musical beat)
  let spb = 1
  try {
    // lazy import to avoid circular deps where possible
    const { makeBeatCode } = require('@/etc/beatcode')
    const bc = makeBeatCode(state.settings)
    spb = bc.secondsPerQuarter()
  } catch (e) {
    const bpm = state.settings.bpm
    const bps = (bpm || 1) / 60
    spb = 1 / bps
  }
  const ret = []
  let bc = 0
  let qc = 0
  let sc = 0

  for (let i = 0; i <= to; i += spb) {
    if (i < from) continue
    bc++
    if (bc === qps) {
      qc++
      bc = 0
    }
    if (qc === spl) {
      sc++
      qc = 0
    }
    ret.push({
      time: i,
      beat: bc,
      quarter: qc,
      square: sc,
      gbeat: Math.round(i / spb),
    })
  }
  return ret
}

export const computeAnchoredTimeWindow = (
  viewLimit,
  anchor,
  currentLength,
  mode
) => {
  const safeLimit = Number.isFinite(viewLimit) ? Math.max(viewLimit, 0) : 0
  const safeAnchor = Number.isFinite(anchor) ? anchor : 0
  const safeCurrentLength = Number.isFinite(currentLength)
    ? currentLength
    : 0

  if (safeLimit <= 0 || safeCurrentLength <= 0) {
    return { start: 0, end: 0 }
  }

  const clamp = (val, min, max) => Math.min(Math.max(val, min), max)

  const ratio = mode === 'zoom-in' ? 1 - ZOOM_STEP : 1 + ZOOM_STEP
  const targetLength = safeCurrentLength * ratio
  const clampedLength = mode === 'zoom-in'
    ? Math.max(Math.min(targetLength, safeLimit), ZOOM_MAX_SEC)
    : Math.min(targetLength, safeLimit)

  let start = safeAnchor - clampedLength / 2
  let end = safeAnchor + clampedLength / 2

  if (start < 0) {
    start = 0
    end = clampedLength
  }
  if (end > safeLimit) {
    end = safeLimit
    start = safeLimit - clampedLength
  }

  start = clamp(start, 0, safeLimit)
  end = clamp(end, start, safeLimit)

  return { start, end }
}
